import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Chip,
  FormControlLabel,
  Checkbox,
  Alert,
  Snackbar,
  Avatar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import {
  Edit,
  Security,
  Person,
  AdminPanelSettings,
  Dashboard,
  Inventory2,
  CreditCard,
  People,
  ReceiptLong,
  BarChart,
  Visibility,
  TrendingUp,
  Person2,
  History,
  Settings,
  Lock,
  LocalShipping,
  SwapHoriz,
  Undo,
  Timeline,
  DeleteForever,
  AccountBalance,
  TrendingDown,
  Block,
  CheckCircle,
  Build,
} from '@mui/icons-material';
import { updateUserPermissions, updateUserType, ensureUserTypesExist, setUserStatus } from '../firebase/userService';
import { logActivity, ACTIONS, ENTITY_TYPES } from '../firebase/activityLogService';
import useFirestoreStore from '../stores/useFirestoreStore';
import { Download } from '@mui/icons-material';

const modules = [
  { id: 'dashboard', name: 'Dashboard', description: 'View dashboard overview and statistics' },
  // ── Purchasing ──
  { id: 'suppliers', name: 'Suppliers', description: 'Manage supplier database' },
  { id: 'purchaseOrders', name: 'Purchase Orders', description: 'Create and manage purchase orders' },
  { id: 'grn', name: 'GRN - Store', description: 'Record stock received from suppliers', deletePermission: 'deleteGRN' },
  { id: 'stockTransfer', name: 'Stock Transfer', description: 'Transfer stock from store to shop inventory' },
  // ── Inventory ──
  { id: 'inventory', name: 'Inventory', description: 'Manage products and stock levels', deletePermission: 'deleteInventory' },
  { id: 'stockHistory', name: 'Stock History', description: 'View batch and stock movement history' },
  { id: 'stockAdjustment', name: 'Stock Adjustment', description: 'Quarterly physical stocktake and manual adjustments' },
  // ── Sales ──
  { id: 'billing', name: 'Billing', description: 'Create and manage invoices', deletePermission: 'deleteBilling' },
  { id: 'returns', name: 'Returns & Refunds', description: 'Process customer returns and refunds' },
  { id: 'customers', name: 'Customers', description: 'Manage customer database', deletePermission: 'deleteCustomers' },
  // ── Finance ──
  { id: 'creditors', name: 'Receivables', description: 'Manage credit payments and customer receivables' },
  { id: 'tradePayables', name: 'Trade Payables', description: 'Track amounts owed to suppliers' },
  { id: 'cashBankLedger', name: 'Cash & Bank Ledger', description: 'Track cash in hand, petty cash and bank balances' },
  { id: 'expenses', name: 'Expenses & Losses', description: 'Track expenses, losses, and stock damage', deletePermission: 'deleteExpenses' },
  // ── Reports ──
  { id: 'reports', name: 'Reports', description: 'View financial reports and analytics' },
  { id: 'viewProfitLoss', name: 'View Profit/Loss', description: 'View profit, loss, and cost data (sensitive)' },
  { id: 'sales', name: 'Analytics', description: 'View sales analytics and reports' },
  // ── System ──
  { id: 'staff', name: 'Staff', description: 'Manage staff members' },
  { id: 'activityLogs', name: 'Activity Logs', description: 'View system activity audit trail' },
  { id: 'settings', name: 'Settings', description: 'Configure billing settings' },
  { id: 'modules', name: 'Module Management', description: 'Manage user permissions (Admin only)' },
  { id: 'adminTools', name: 'Admin Tools', description: 'Batch repair, data migration and advanced admin utilities (Admin only)', adminOnly: true },
];

const ModuleManagement = ({ currentUser }) => {
  // Read from global store — no extra listeners
  const users = useFirestoreStore((state) => state.users);
  const loading = useFirestoreStore((state) => state.loading.users);
  const bills = useFirestoreStore((state) => state.bills);
  const customers = useFirestoreStore((state) => state.customers);
  const inventory = useFirestoreStore((state) => state.inventory);
  const expenses = useFirestoreStore((state) => state.expenses);
  const expenseCategories = useFirestoreStore((state) => state.expenseCategories);
  const creditors = useFirestoreStore((state) => state.creditors);
  const returns = useFirestoreStore((state) => state.returns);
  const suppliers = useFirestoreStore((state) => state.suppliers);
  const purchaseOrders = useFirestoreStore((state) => state.purchaseOrders);
  const grns = useFirestoreStore((state) => state.grns);
  const stockTransfers = useFirestoreStore((state) => state.stockTransfers);
  const tradePayables = useFirestoreStore((state) => state.tradePayables);

  const [showModal, setShowModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [tempPermissions, setTempPermissions] = useState({});
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [backingUp, setBackingUp] = useState(false);

  const handleBackup = () => {
    setBackingUp(true);
    try {
      const backup = {
        exportedAt: new Date().toISOString(),
        exportedBy: currentUser?.email || 'admin',
        version: '1.0',
        collections: {
          bills: { count: bills.length, data: bills },
          customers: { count: customers.length, data: customers },
          inventory: { count: inventory.length, data: inventory },
          expenses: { count: expenses.length, data: expenses },
          expenseCategories: { count: expenseCategories.length, data: expenseCategories },
          creditors: { count: creditors.length, data: creditors },
          returns: { count: returns.length, data: returns },
          suppliers: { count: suppliers.length, data: suppliers },
          purchaseOrders: { count: purchaseOrders.length, data: purchaseOrders },
          grns: { count: grns.length, data: grns },
          stockTransfers: { count: stockTransfers.length, data: stockTransfers },
          tradePayables: { count: tradePayables.length, data: tradePayables },
          users: { count: users.length, data: users },
        },
        summary: {
          totalDocuments:
            bills.length + customers.length + inventory.length +
            expenses.length + expenseCategories.length + creditors.length +
            returns.length + suppliers.length + purchaseOrders.length +
            grns.length + stockTransfers.length + tradePayables.length + users.length,
        },
      };

      const json = JSON.stringify(backup, null, 2);
      const blob = new Blob([json], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `PerfumeStories_Backup_${new Date().toISOString().split('T')[0]}.json`;
      a.click();
      URL.revokeObjectURL(url);

      setSnackbar({
        open: true,
        message: `Backup downloaded — ${backup.summary.totalDocuments} documents across ${Object.keys(backup.collections).length} collections`,
        severity: 'success',
      });
    } catch (err) {
      setSnackbar({ open: true, message: 'Backup failed: ' + err.message, severity: 'error' });
    } finally {
      setBackingUp(false);
    }
  };

  useEffect(() => {
    // Ensure all users have userType field
    ensureUserTypesExist();
  }, []);

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleEditPermissions = (user) => {
    setSelectedUser(user);
    setTempPermissions(user.permissions || {});
    setShowModal(true);
  };

  const handlePermissionChange = (module) => {
    setTempPermissions(prev => ({
      ...prev,
      [module]: !prev[module]
    }));
  };

  const handleUserTypeChange = async (type) => {
    if (type === 'admin') {
      // Admin gets all permissions including adminTools
      const allPerms = {};
      modules.forEach(m => { allPerms[m.id] = true; });
      setTempPermissions(allPerms);
    } else {
      // Staff gets limited permissions
      setTempPermissions(prev => ({
        ...prev,
        staff: false,
        activityLogs: false,
        settings: false,
        modules: false,
      }));
    }
    
    // Update user type immediately
    const result = await updateUserType(selectedUser.id, type);
    if (result.success) {
      showSnackbar(`User type changed to ${type}`);
      
      // Log activity
      await logActivity({
        userId: currentUser?.uid,
        userEmail: currentUser?.email,
        userName: currentUser?.displayName || currentUser?.email,
        action: ACTIONS.UPDATED_PERMISSIONS,
        entityType: ENTITY_TYPES.STAFF,
        entityId: selectedUser.id,
        entityName: selectedUser.name,
        details: { userType: type }
      });
    } else {
      showSnackbar(result.error || 'Failed to update user type', 'error');
    }
  };

  const handleSave = async () => {
    if (!selectedUser.id) {
      showSnackbar('User ID not found', 'error');
      return;
    }

    // Update permissions in Firestore
    const result = await updateUserPermissions(selectedUser.id, tempPermissions);
    
    if (result.success) {
      showSnackbar('Permissions updated successfully');
      closeModal();
      
      // Log activity
      await logActivity({
        userId: currentUser?.uid,
        userEmail: currentUser?.email,
        userName: currentUser?.displayName || currentUser?.email,
        action: ACTIONS.UPDATED_PERMISSIONS,
        entityType: ENTITY_TYPES.STAFF,
        entityId: selectedUser.id,
        entityName: selectedUser.name,
        details: { 
          modulesEnabled: Object.keys(tempPermissions).filter(k => tempPermissions[k]).length 
        }
      });
    } else {
      showSnackbar(result.error || 'Failed to update permissions', 'error');
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedUser(null);
    setTempPermissions({});
  };

  const handleToggleStatus = async (user) => {
    const newDisabled = !user.disabled;
    const action = newDisabled ? 'disable' : 'enable';

    if (!window.confirm(`Are you sure you want to ${action} ${user.name || user.email}?`)) return;

    const result = await setUserStatus(user.id, newDisabled);
    if (result.success) {
      showSnackbar(`User ${action}d successfully`);
      await logActivity({
        userId: currentUser?.uid,
        userEmail: currentUser?.email,
        userName: currentUser?.displayName || currentUser?.email,
        action: ACTIONS.UPDATED_PERMISSIONS,
        entityType: ENTITY_TYPES.STAFF,
        entityId: user.id,
        entityName: user.name,
        details: { status: newDisabled ? 'disabled' : 'enabled' },
      });
    } else {
      showSnackbar(result.error || `Failed to ${action} user`, 'error');
    }
  };

  // Count only the module-level permissions (not delete sub-perms, userType, updatedAt, etc.)
  // For admins, always return full count since they have everything implicitly.
  const getPermissionCount = (user) => {
    if ((user.userType || user.permissions?.userType || 'staff') === 'admin') {
      return modules.length;
    }
    if (!user.permissions) return 0;
    return modules.filter(m => user.permissions[m.id] === true).length;
  };

  const getModuleIcon = (moduleId) => {
    const iconProps = { sx: { fontSize: 28 } };
    const iconMap = {
      dashboard: <Dashboard {...iconProps} />,
      suppliers: <People {...iconProps} />,
      purchaseOrders: <ReceiptLong {...iconProps} />,
      grn: <LocalShipping {...iconProps} />,
      stockTransfer: <SwapHoriz {...iconProps} />,
      inventory: <Inventory2 {...iconProps} />,
      stockHistory: <Timeline {...iconProps} />,
      stockAdjustment: <TrendingDown {...iconProps} />,
      billing: <CreditCard {...iconProps} />,
      returns: <Undo {...iconProps} />,
      creditors: <People {...iconProps} />,
      cashBankLedger: <AccountBalance {...iconProps} />,
      tradePayables: <TrendingDown {...iconProps} />,
      tradeReceivables: <TrendingUp {...iconProps} />,
      expenses: <ReceiptLong {...iconProps} />,
      reports: <BarChart {...iconProps} />,
      viewProfitLoss: <Visibility {...iconProps} />,
      sales: <TrendingUp {...iconProps} />,
      customers: <Person2 {...iconProps} />,
      staff: <People {...iconProps} />,
      activityLogs: <History {...iconProps} />,
      settings: <Settings {...iconProps} />,
      modules: <Lock {...iconProps} />,
      adminTools: <Build {...iconProps} />,
      deleteBilling: <DeleteForever {...iconProps} />,
      deleteInventory: <DeleteForever {...iconProps} />,
      deleteGRN: <DeleteForever {...iconProps} />,
      deleteExpenses: <DeleteForever {...iconProps} />,
      deleteCustomers: <DeleteForever {...iconProps} />,
    };
    return iconMap[moduleId] || <Security {...iconProps} />;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  const adminCount = users.filter(u => (u.userType || 'staff') === 'admin').length;
  const staffCount = users.filter(u => (u.userType || 'staff') === 'staff').length;

  const StatCard = ({ icon, value, label, subtitle }) => (
    <Card sx={{ height: '100%', position: 'relative', overflow: 'visible' }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box
            sx={{
              p: 1.5,
              borderRadius: 2,
              backgroundColor: 'action.hover',
              color: 'text.primary',
              display: 'flex',
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            {icon}
          </Box>
        </Box>
        <Typography variant="h4" fontWeight={700} gutterBottom color="text.primary">
          {value}
        </Typography>
        <Typography variant="body2" color="text.secondary" fontWeight={600}>
          {label}
        </Typography>
        {subtitle && (
          <Typography variant="caption" color="text.secondary">
            {subtitle}
          </Typography>
        )}
      </CardContent>
    </Card>
  );

  return (
    <Box>
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <AdminPanelSettings sx={{ color: 'text.primary' }} />
            <Typography variant="h4" fontWeight={700}>
              Module Management
            </Typography>
          </Box>
          <Typography variant="body1" color="text.secondary">
            Manage user permissions and access control
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={backingUp ? <CircularProgress size={18} /> : <Download />}
          onClick={handleBackup}
          disabled={backingUp}
          size="small"
          color="primary"
        >
          {backingUp ? 'Backing up...' : 'Download Backup'}
        </Button>
      </Box>

      <Grid container spacing={4} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={<Person />}
            value={users.length}
            label="Total Users"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={<AdminPanelSettings />}
            value={adminCount}
            label="Administrators"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={<Person />}
            value={staffCount}
            label="Staff Members"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={<Security />}
            value={modules.length}
            label="Available Modules"
          />
        </Grid>
      </Grid>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" fontWeight={600}>
              Available Modules
            </Typography>
          </Box>
          <Grid container spacing={2}>
            {modules.map((module) => (
              <Grid item xs={12} sm={6} md={4} key={module.id}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2.5,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2,
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1.5,
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      borderColor: 'primary.main',
                      backgroundColor: 'action.hover',
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 48,
                      height: 48,
                      borderRadius: 1.5,
                      backgroundColor: 'background.paper',
                      color: 'text.primary',
                      border: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    {getModuleIcon(module.id)}
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                      {module.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {module.description}
                    </Typography>
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="h6" fontWeight={600} gutterBottom>
            User Permissions
          </Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell><strong>User</strong></TableCell>
                  <TableCell><strong>User Type</strong></TableCell>
                  <TableCell align="center"><strong>Modules Access</strong></TableCell>
                  <TableCell><strong>Status</strong></TableCell>
                  <TableCell align="center"><strong>Actions</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map((user) => {
                  const permCount = getPermissionCount(user);
                  return (
                    <TableRow key={user.id} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Avatar sx={{ bgcolor: 'background.paper', color: 'text.primary', fontWeight: 700, fontSize: '0.9rem', border: '1px solid', borderColor: 'divider' }}>
                            {user.name ? user.name.split(' ').map(n => n.charAt(0)).join('').toUpperCase() : 'U'}
                          </Avatar>
                          <Box>
                            <Typography variant="body2" fontWeight={600}>
                              {user.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {user.email}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={(user.userType || 'staff') === 'admin' ? 'Administrator' : 'Staff'}
                          size="small"
                          color="default"
                          icon={(user.userType || 'staff') === 'admin' ? <AdminPanelSettings sx={{ color: '#000 !important' }} /> : <Person sx={{ color: '#000 !important' }} />}
                          variant="filled"
                          sx={{
                            fontWeight: 700,
                            backgroundColor: 'background.paper',
                            color: 'text.primary',
                            borderRadius: '20px',
                            border: '1px solid',
                            borderColor: 'divider',
                          }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={`${permCount} / ${modules.length}`}
                          size="small"
                          color="default"
                          sx={{
                            backgroundColor: 'background.paper',
                            color: 'text.primary',
                            fontWeight: 700,
                            border: '1px solid',
                            borderColor: 'divider',
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={user.disabled ? 'Inactive' : 'Active'}
                          size="small"
                          color={user.disabled ? 'default' : 'success'}
                          sx={{
                            fontWeight: 700,
                            '& .MuiChip-label': { px: 1 },
                          }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                          <Tooltip title="Edit Permissions">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleEditPermissions(user)}
                            >
                              <Edit fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={user.disabled ? 'Enable User' : 'Disable User'}>
                            <IconButton
                              size="small"
                              color={user.disabled ? 'success' : 'error'}
                              onClick={() => handleToggleStatus(user)}
                            >
                              {user.disabled
                                ? <CheckCircle fontSize="small" />
                                : <Block fontSize="small" />}
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {users.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">No users found</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      <Dialog open={showModal} onClose={closeModal} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Security color="primary" />
            <Typography variant="h6">
              Manage Permissions - {selectedUser?.name}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel>User Type</InputLabel>
              <Select
                value={selectedUser?.userType || 'staff'}
                label="User Type"
                onChange={(e) => handleUserTypeChange(e.target.value)}
              >
                <MenuItem value="staff">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Person fontSize="small" />
                    Staff Member
                  </Box>
                </MenuItem>
                <MenuItem value="admin">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AdminPanelSettings fontSize="small" />
                    Administrator
                  </Box>
                </MenuItem>
              </Select>
            </FormControl>

            {(selectedUser?.userType || 'staff') === 'admin' && (
              <Alert severity="info" sx={{ mb: 3 }}>
                Administrators have full access to all modules. Only <strong>Admin Tools</strong> can be individually toggled for admin users.
              </Alert>
            )}

            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              Module Access Permissions
            </Typography>
            <Grid container spacing={2}>
              {modules.map((module) => {
                const isAdminOnlyModule = !!module.adminOnly;
                const userIsAdmin = (selectedUser?.userType || 'staff') === 'admin';

                // For admin users:
                //   - adminTools is the ONLY module they can toggle on/off
                //   - all other modules are locked checked (admins always have them)
                // For staff users:
                //   - adminOnly modules and system modules are locked unchecked
                const isDisabled = userIsAdmin
                  ? !isAdminOnlyModule   // admins: only adminTools is editable
                  : (isAdminOnlyModule || ['staff', 'activityLogs', 'settings', 'modules'].includes(module.id));

                const isChecked = userIsAdmin
                  ? (isAdminOnlyModule ? (tempPermissions[module.id] ?? true) : true)
                  : (tempPermissions[module.id] || false);
                const hasDelete = !!module.deletePermission;
                const isDeleteChecked = hasDelete ? (tempPermissions[module.deletePermission] || false) : false;
                const isDeleteDisabled = isDisabled || !isChecked;

                return (
                  <Grid item xs={12} sm={6} key={module.id}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2.5,
                        border: '2px solid',
                        borderColor: isChecked ? 'primary.main' : 'divider',
                        borderRadius: 2,
                        backgroundColor: isChecked ? 'rgba(25, 118, 210, 0.08)' : 'transparent',
                        opacity: isDisabled && (selectedUser?.userType || 'staff') === 'staff' ? 0.5 : 1,
                        cursor: isDisabled ? 'not-allowed' : 'pointer',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          borderColor: isDisabled ? 'divider' : 'primary.main',
                          backgroundColor: isDisabled ? 'transparent' : 'rgba(25, 118, 210, 0.04)',
                        },
                      }}
                    >
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={isChecked}
                            onChange={() => handlePermissionChange(module.id)}
                            disabled={isDisabled}
                          />
                        }
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, width: '100%' }}>
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: 40,
                                height: 40,
                                borderRadius: 1.5,
                                backgroundColor: isChecked ? 'primary.main' : '#fff',
                                color: isChecked ? '#fff' : '#000',
                                flexShrink: 0,
                              }}
                            >
                              {getModuleIcon(module.id)}
                            </Box>
                            <Box sx={{ flex: 1 }}>
                              <Typography variant="body2" fontWeight={600}>
                                {module.name}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                {module.description}
                              </Typography>
                            </Box>
                          </Box>
                        }
                        sx={{ width: '100%', m: 0 }}
                      />
                      {hasDelete && (
                        <Box
                          sx={{
                            mt: 1.5,
                            ml: 4,
                            pl: 1,
                            borderLeft: '2px solid',
                            borderColor: isDeleteChecked ? 'error.main' : 'divider',
                          }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={isDeleteChecked}
                                onChange={() => handlePermissionChange(module.deletePermission)}
                                disabled={isDeleteDisabled}
                                size="small"
                                sx={{ color: 'error.main', '&.Mui-checked': { color: 'error.main' } }}
                              />
                            }
                            label={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <DeleteForever sx={{ fontSize: 14, color: isDeleteChecked ? 'error.main' : 'text.disabled' }} />
                                <Typography variant="caption" color={isDeleteChecked ? 'error.main' : 'text.secondary'} fontWeight={600}>
                                  Allow Delete
                                </Typography>
                              </Box>
                            }
                            sx={{ m: 0 }}
                          />
                        </Box>
                      )}
                    </Paper>
                  </Grid>
                );
              })}
            </Grid>

            {(selectedUser?.userType || 'staff') === 'staff' && (
              <Alert severity="warning" sx={{ mt: 3 }}>
                Staff members cannot access Staff Management, Activity Logs, Settings, Module Management, or Admin Tools modules.
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeModal}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} startIcon={<Security />}>
            Save Permissions
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ModuleManagement;
